import { TranslationLanguage, getRootLang } from "@hopper-b2b/i18n";

export const supportedLanguageCodes: Partial<
  Record<TranslationLanguage, string>
> = {
  [TranslationLanguage.en]: "EN",
  [TranslationLanguage.es]: "ES",
  [TranslationLanguage.fr]: "FR",
};

export const supportedLanguageNames: Partial<
  Record<TranslationLanguage, string>
> = {
  [TranslationLanguage.en]: "English",
  [TranslationLanguage.es]: "Español",
  [TranslationLanguage.fr]: "Français",
};

export const supportedLanguagePaths: Partial<
  Record<TranslationLanguage, string | null>
> = {
  [TranslationLanguage.en]: null,
  [TranslationLanguage.es]: "es",
  [TranslationLanguage.fr]: "fr",
};

export const defaultLanguage = TranslationLanguage.en;

const getCurrentLanguageFromUrl = (): TranslationLanguage | undefined => {
  const splitPath =
    typeof window !== "undefined" ? window.location.pathname.split("/") : [];
  const currentLanguage = splitPath[1];

  return currentLanguage && currentLanguage in TranslationLanguage
    ? TranslationLanguage[currentLanguage as TranslationLanguage]
    : undefined;
};

export const getCurrentLanguage = (): TranslationLanguage =>
  getCurrentLanguageFromUrl() ??
  TranslationLanguage[getRootLang(defaultLanguage) as TranslationLanguage] ??
  defaultLanguage;

export const getLanguagePath = (lang: TranslationLanguage): string => {
  const langPath = supportedLanguagePaths[lang];
  const splitPath =
    typeof window !== "undefined" ? window.location.pathname.split("/") : [""];

  const currentLangPath = splitPath[1];
  const shouldDelete =
    currentLangPath &&
    !!TranslationLanguage[currentLangPath as TranslationLanguage];

  if (!langPath) {
    splitPath.splice(1, shouldDelete ? 1 : 0);
  } else {
    splitPath.splice(1, shouldDelete ? 1 : 0, langPath);
  }

  const languagePath = splitPath.join("/");

  return !languagePath || languagePath === "" ? "/" : languagePath;
};

/**
 * Maps Whopper's TranslationLanguage to the commerce-portal platform locale format.
 * Used when redirecting to the HTS commerce platform (stays, cars, etc.).
 *
 * Mapping:
 * - en → (no prefix, English is the default)
 * - es → es-419 (Latin American Spanish)
 * - fr → fr-CA (Canadian French)
 *
 * French maps to fr-CA rather than fr-FR because hopper.com's French-speaking
 * audience is predominantly Canadian. Both locales are in the hopper-app
 * tenant's supportedLocales, so either resolves; fr-CA gives the
 * region-appropriate formatting for that audience.
 */
export const platformLocaleMap: Partial<Record<TranslationLanguage, string>> = {
  [TranslationLanguage.en]: "", // English is the default, no prefix needed
  [TranslationLanguage.es]: "es-419",
  [TranslationLanguage.fr]: "fr-CA",
};

/**
 * Gets the platform locale prefix for the current language.
 * Returns an empty string for English (default), or the locale path prefix for other languages.
 *
 * @example
 * // If current language is French:
 * getPlatformLocalePrefix() // returns "/fr-CA"
 *
 * // If current language is English:
 * getPlatformLocalePrefix() // returns ""
 */
export const getPlatformLocalePrefix = (): string => {
  const currentLang = getCurrentLanguage();
  const platformLocale = platformLocaleMap[currentLang];
  return platformLocale ? `/${platformLocale}` : "";
};

/**
 * Prepends the platform locale prefix to a given path.
 * Use this when constructing URLs that redirect to the commerce-portal platform.
 *
 * @param path - The path to prepend the locale to (should start with /)
 * @returns The path with the platform locale prefix
 *
 * @example
 * // If current language is French:
 * withPlatformLocale("/stays/availability?platform=hts") // returns "/fr-CA/stays/availability?platform=hts"
 *
 * // If current language is English:
 * withPlatformLocale("/stays/availability?platform=hts") // returns "/stays/availability?platform=hts"
 */
export const withPlatformLocale = (path: string): string => {
  const prefix = getPlatformLocalePrefix();
  return `${prefix}${path}`;
};
